import React from 'react';
import PropTypes from 'prop-types';

import useComponentDidMount from 'Hooks/useComponentDidMount';

const NavigationDrawerContext = React.createContext(undefined);

// Actions
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';

const NAV_DRAWER_REFRESH_STARTED = 'client-refresh-started';
const NAV_DRAWER_REFRESH_SUCCEEDED = 'client-refresh-succeeded';
const NAV_DRAWER_REFRESH_FAILED = 'client-refresh-failed';

const SHOW_NAVIGATION = 'show-navigation';
const HIDE_NAVIGATION = 'hide-navigation';

// Initialize State
const init = () => {
  // IMPORTANT: Please be cautious of adding additional state here.
  // Is there a need for it to be loaded concurrently with the client?
  // If not, it may make sense in a separate context.
  return {
    isInitializing: true,
    initializingError: null,
    navigationCollection: null,
    isNavigationCollectionRefreshing: false,
    navigationCollectionRefreshError: null,
    showNavigation: false,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      const [resource] = action.navigationCollection;

      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        navigationCollection: action.navigationCollection,
        navigationResource: resource,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case NAV_DRAWER_REFRESH_STARTED: {
      return {
        ...state,
        isNavigationCollectionRefreshing: true,
        navigationCollectionRefreshError: null,
      };
    }
    case NAV_DRAWER_REFRESH_SUCCEEDED: {
      const [resource] = action.navigationCollection;

      return {
        ...state,
        isNavigationCollectionRefreshing: false,
        navigationCollectionRefreshError: null,
        navigationCollection: action.navigationCollection,
        navigationResource: resource,
      };
    }
    case NAV_DRAWER_REFRESH_FAILED: {
      return {
        ...state,
        isNavigationCollectionRefreshing: false,
        navigationCollectionRefreshError: action.navigationCollectionRefreshError,
      };
    }
    case SHOW_NAVIGATION: {
      return {
        ...state,
        showNavigation: true,
      };
    }
    case HIDE_NAVIGATION: {
      return {
        ...state,
        showNavigation: false,
      };
    }
    default: {
      throw new Error(`Unhandled NavigationDrawerContext action type: ${action.type}`);
    }
  }
};

const NavigationDrawerContextProvider = ({
  children,
  onboardingNavigationLink,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);

  const refreshNavigationCollection = React.useCallback(async (link) => {
    try {
      dispatch({ type: NAV_DRAWER_REFRESH_STARTED });
      const coll = await link.get();
      dispatch({ type: NAV_DRAWER_REFRESH_SUCCEEDED, navigationCollection: coll });
    } catch (err) {
      dispatch({ type: NAV_DRAWER_REFRESH_FAILED, navigationCollectionRefreshError: err });
    }
  }, []);

  const handleShowNavigation = React.useCallback(async () => {
    await refreshNavigationCollection(onboardingNavigationLink);
    dispatch({ type: SHOW_NAVIGATION });
  }, [onboardingNavigationLink, refreshNavigationCollection]);

  const handleHideNavigation = React.useCallback(async () => {
    dispatch({ type: HIDE_NAVIGATION });
  }, []);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      navigationCollection: state.navigationCollection,
      navigationResource: state.navigationResource,
      isNavigationCollectionRefreshing: state.isNavigationCollectionRefreshing,
      navigationCollectionRefreshError: state.navigationCollectionRefreshError,
      showNavigation: state.showNavigation,
      refreshNavigationCollection,
      handleShowNavigation,
      handleHideNavigation,
    };
  }, [state, refreshNavigationCollection, handleShowNavigation, handleHideNavigation]);

  useComponentDidMount(async () => {
    try {
      if (!onboardingNavigationLink) {
        return;
      }

      dispatch({ type: INITIALIZE_STARTED });
      const coll = await onboardingNavigationLink.get();

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        navigationCollection: coll,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
    }
  });

  return (
    <NavigationDrawerContext.Provider value={memoizedValue}>
      {children}
    </NavigationDrawerContext.Provider>
  );
};

const useNavigationDrawerContext = () => {
  const context = React.useContext(NavigationDrawerContext);
  if (context === undefined) {
    throw new Error('useNavigationDrawerContext must be used within a NavigationDrawerContextProvider');
  }
  return context;
};

NavigationDrawerContextProvider.defaultProps = {
  onboardingNavigationLink: null,
};

NavigationDrawerContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  onboardingNavigationLink: PropTypes.object,
};

export {
  NavigationDrawerContextProvider,
  useNavigationDrawerContext,
};
